/**
 * Flexi-Admin Mobile (PWA) — minimal cockpit for the admin on the go.
 *
 * Features:
 *  - Email/password login (reuses /api/auth/login JWT, stored in localStorage)
 *  - Big "Ouvrir Porte X" cards for every active device
 *  - Today's bookings list (highlights in-progress sessions)
 *  - Raspberry Pi status badge (online/offline + heartbeat)
 *  - Auto-refresh every 30 s
 *
 * UX choices (per user prefs): clear/sober design consistent with desktop admin,
 * no top nav (chrome hidden via App.js hideChrome whitelist), one-tap actions.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DoorOpen, LogOut, RefreshCw, Loader2, Calendar, Wifi, WifiOff,
  ShieldCheck, Clock, User, Bell, BellOff,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TOKEN_KEY = 'admin_mobile_token';
const AUTOREFRESH_MS = 30000;

function todayISO() {
  // YYYY-MM-DD in Brussels time (sufficient for filtering bookings by date string)
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Brussels' });
}

function nowHM() {
  // HH:MM in Brussels — for highlighting in-progress sessions
  return new Date().toLocaleTimeString('fr-BE', {
    timeZone: 'Europe/Brussels', hour: '2-digit', minute: '2-digit',
  });
}

function fmtAgo(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'à l\'instant';
  if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
  return `il y a ${Math.round(diff / 86400)} j`;
}

export default function AdminMobilePage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Validate token on mount — if it's expired / not admin, force re-login
  useEffect(() => {
    if (!token) { setUser(null); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.data?.role !== 'admin' && !r.data?.is_admin) {
          toast.error('Compte non administrateur');
          localStorage.removeItem(TOKEN_KEY);
          setToken('');
          setUser(null);
        } else {
          setUser(r.data);
        }
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
        setUser(null);
      }
    })();
  }, [token]);

  const handleLogin = (newToken, userData) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(userData);
  };
  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setUser(null);
  };

  return (
    <div className="min-h-screen bg-[#F0EFEA] text-[#1A3C34] font-body" data-testid="admin-mobile-page">
      {!token || !user ? (
        <LoginScreen onLogin={handleLogin} />
      ) : (
        <Dashboard
          token={token}
          user={user}
          onLogout={handleLogout}
          onBackHome={() => navigate('/')}
        />
      )}
    </div>
  );
}

/* ───────────────────────── LOGIN ───────────────────────── */

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    if (!email || !pw) { setErr('Email et mot de passe requis'); return; }
    setErr('');
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password: pw });
      const tk = r.data?.token || r.data?.access_token;
      const u = r.data?.user || r.data;
      if (!tk) throw new Error('Pas de token');
      const isAdmin = u?.role === 'admin' || u?.is_admin === true;
      if (!isAdmin) { setErr('Compte non administrateur'); setLoading(false); return; }
      onLogin(tk, u);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm bg-white border border-[#E2E2DF] rounded-2xl p-7 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#1A3C34] flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-heading font-semibold leading-tight">Flexi-Admin</h1>
            <p className="text-xs text-[#737373]">Cockpit mobile</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs text-[#737373] mb-1">Email administrateur</label>
            <input
              type="email" autoComplete="email" inputMode="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-12 border border-[#E2E2DF] rounded-md px-3 text-base focus:outline-none focus:border-[#1A3C34]"
              data-testid="admin-mobile-email" required
            />
          </div>
          <div>
            <label className="block text-xs text-[#737373] mb-1">Mot de passe</label>
            <input
              type="password" autoComplete="current-password"
              value={pw} onChange={(e) => setPw(e.target.value)}
              className="w-full h-12 border border-[#E2E2DF] rounded-md px-3 text-base focus:outline-none focus:border-[#1A3C34]"
              data-testid="admin-mobile-password" required
            />
          </div>
          {err && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2" data-testid="admin-mobile-err">
              {err}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-mobile-login-btn"
            className="w-full h-12 bg-[#1A3C34] text-white rounded-md font-medium text-base disabled:opacity-50 active:scale-[0.98] transition"
          >
            {loading ? (<><Loader2 className="w-4 h-4 inline-block animate-spin mr-2" />Connexion…</>) : 'Se connecter'}
          </button>
        </form>

        <p className="text-[10px] text-[#737373] mt-5 text-center">
          Ajoute ce site à ton écran d'accueil pour un accès rapide.
        </p>
      </div>
    </div>
  );
}

/* ───────────────────────── DASHBOARD ───────────────────────── */

function Dashboard({ token, user, onLogout, onBackHome }) {
  const [devices, setDevices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const timerRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };
  const today = todayISO();

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [dRes, bRes] = await Promise.all([
        axios.get(`${API}/admin/door-devices`, { headers }),
        axios.get(`${API}/admin/bookings`, { headers, params: { status_filter: 'confirmed' } }),
      ]);
      setDevices(Array.isArray(dRes.data) ? dRes.data : []);
      // Keep only today's bookings, sort by start_time
      const today_bookings = (bRes.data || [])
        .filter((b) => b.date === today)
        .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
      setBookings(today_bookings);
      setLastSync(new Date());
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error('Session expirée');
        onLogout();
        return;
      }
      if (!silent) toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load(true), AUTOREFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const openDoor = async (device) => {
    const label = device.label || device.device_id;
    if (!window.confirm(`Ouvrir « ${label} » pendant 5 secondes ?`)) return;
    try {
      await axios.post(`${API}/admin/door/open`, {
        device_id: device.device_id,
        duration_s: 5,
      }, { headers });
      toast.success(`✓ ${label} : commande envoyée`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Erreur sur ${label}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1A3C34] animate-spin" />
      </div>
    );
  }

  const currentHM = nowHM();
  const liveCount = bookings.filter((b) => isLive(b, currentHM)).length;

  return (
    <div className="pb-10">
      {/* Sticky header */}
      <header className="sticky top-0 z-10 bg-[#1A3C34] text-white px-4 py-3 shadow-md flex items-center justify-between">
        <div>
          <h1 className="text-base font-heading font-semibold leading-tight">Flexi-Admin</h1>
          <p className="text-[10px] text-white/70">
            {(user?.name || user?.email || '').split(' ')[0]} · {lastSync ? `sync ${fmtAgo(lastSync.toISOString())}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load()}
            disabled={refreshing}
            data-testid="admin-mobile-refresh"
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95"
            aria-label="Rafraîchir"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={onLogout}
            data-testid="admin-mobile-logout"
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95"
            aria-label="Déconnexion"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Stat strip */}
      <div className="px-4 py-3 grid grid-cols-2 gap-3">
        <StatCard
          label="En cours"
          value={liveCount}
          accent="bg-emerald-50 border-emerald-200 text-emerald-800"
          icon={<User className="w-4 h-4" />}
        />
        <StatCard
          label="Aujourd'hui"
          value={bookings.length}
          accent="bg-white border-[#E2E2DF] text-[#1A3C34]"
          icon={<Calendar className="w-4 h-4" />}
        />
      </div>

      {/* Push notifications block */}
      <div className="px-4">
        <PushNotificationsToggle headers={headers} />
      </div>

      {/* Doors section */}
      <section className="px-4 mt-2">
        <SectionHeader title="Portes" icon={<DoorOpen className="w-4 h-4" />} count={devices.length} />
        {devices.length === 0 ? (
          <p className="text-sm text-[#737373] italic py-4">Aucune porte enregistrée.</p>
        ) : (
          <div className="space-y-2.5">
            {devices.map((d) => (
              <DoorCard key={d.device_id} device={d} onOpen={() => openDoor(d)} />
            ))}
          </div>
        )}
      </section>

      {/* Bookings section */}
      <section className="px-4 mt-6">
        <SectionHeader
          title={`Aujourd'hui (${today.split('-').reverse().slice(0, 2).join('/')})`}
          icon={<Calendar className="w-4 h-4" />}
          count={bookings.length}
        />
        {bookings.length === 0 ? (
          <p className="text-sm text-[#737373] italic py-4">Aucune réservation aujourd'hui.</p>
        ) : (
          <div className="space-y-2">
            {bookings.map((b) => (
              <BookingRow key={b.booking_id} b={b} live={isLive(b, currentHM)} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* ───────── small visual components ───────── */

const SectionHeader = ({ title, icon, count }) => (
  <div className="flex items-center justify-between mb-2 mt-1">
    <h2 className="text-[11px] uppercase tracking-wider font-medium text-[#737373] flex items-center gap-1.5">
      {icon} {title}
    </h2>
    {typeof count === 'number' && (
      <span className="text-[10px] text-[#737373]">{count}</span>
    )}
  </div>
);

const StatCard = ({ label, value, accent, icon }) => (
  <div className={`rounded-xl border px-3 py-2.5 ${accent}`}>
    <div className="text-[10px] uppercase tracking-wider opacity-70 flex items-center gap-1">{icon} {label}</div>
    <div className="text-2xl font-heading font-semibold leading-tight mt-0.5">{value}</div>
  </div>
);

/* ───────── Push notifications toggle ───────── */

function urlBase64ToUint8Array(b64) {
  const padded = b64 + '==='.slice((b64.length + 3) % 4);
  const safe = padded.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(safe);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function PushNotificationsToggle({ headers }) {
  const [supported, setSupported] = useState(false);
  const [perm, setPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ok = typeof window !== 'undefined'
      && 'serviceWorker' in navigator
      && 'PushManager' in window
      && 'Notification' in window;
    setSupported(ok);
    if (!ok) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch (_) {}
    })();
  }, []);

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // 1) Permission
      let p = Notification.permission;
      if (p === 'default') p = await Notification.requestPermission();
      setPerm(p);
      if (p !== 'granted') {
        toast.error('Notifications refusées. Active-les dans les réglages du navigateur.');
        return;
      }
      // 2) Get VAPID public key from server
      const r = await axios.get(`${API}/push/public-key`);
      const pubKey = r.data?.public_key;
      if (!pubKey) {
        toast.error('Push non configuré côté serveur');
        return;
      }
      // 3) Subscribe via Service Worker
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(pubKey),
      });
      // 4) Send to backend
      const subJson = sub.toJSON();
      await axios.post(`${API}/admin/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      }, { headers });
      setSubscribed(true);
      toast.success('✓ Notifications activées');
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Erreur d\'activation');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const subJson = sub.toJSON();
        await axios.post(`${API}/admin/push/unsubscribe`, { endpoint: subJson.endpoint, keys: subJson.keys }, { headers });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success('Notifications désactivées');
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setBusy(false);
    }
  };

  const testPush = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/push/test`, {}, { headers });
      const sent = r.data?.sent ?? 0;
      if (sent > 0) toast.success(`✓ Test envoyé (${sent} abonné${sent > 1 ? 's' : ''})`);
      else toast.warning('Aucun abonné — active d\'abord les notifications');
    } catch (e) {
      toast.error('Erreur test');
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="push-not-supported">
        <BellOff className="w-3.5 h-3.5 inline mr-1" />
        Ton navigateur ne supporte pas les notifications. Installe d'abord la PWA via "Ajouter à l'écran d'accueil".
      </div>
    );
  }
  if (perm === 'denied') {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800" data-testid="push-denied">
        <BellOff className="w-3.5 h-3.5 inline mr-1" />
        Notifications bloquées. Va dans Réglages → Safari/Chrome → autoriser pour ce site.
      </div>
    );
  }

  return (
    <div
      data-testid="push-toggle"
      className={`rounded-xl border px-3 py-2.5 flex items-center justify-between gap-2 ${
        subscribed ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-[#E2E2DF]'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {subscribed ? <Bell className="w-4 h-4 text-emerald-700" /> : <BellOff className="w-4 h-4 text-[#737373]" />}
        <div className="min-w-0">
          <p className="text-xs font-medium text-[#1A3C34]">
            {subscribed ? 'Notifications actives' : 'Notifications désactivées'}
          </p>
          <p className="text-[10px] text-[#737373] truncate">
            {subscribed ? 'Ping à chaque ouverture de porte' : 'Active pour être averti(e) en temps réel'}
          </p>
        </div>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        {subscribed && (
          <button onClick={testPush} disabled={busy}
            data-testid="push-test-btn"
            className="h-9 px-3 text-xs rounded-md border border-[#E2E2DF] text-[#737373] bg-white active:scale-95">
            Tester
          </button>
        )}
        <button
          onClick={subscribed ? disable : enable}
          disabled={busy}
          data-testid={subscribed ? 'push-disable-btn' : 'push-enable-btn'}
          className={`h-9 px-3 text-xs rounded-md font-medium active:scale-95 ${
            subscribed ? 'bg-white border border-[#E2E2DF] text-[#737373]' : 'bg-[#1A3C34] text-white'
          }`}
        >
          {busy ? '…' : subscribed ? 'Désactiver' : 'Activer'}
        </button>
      </div>
    </div>
  );
}


function DoorCard({ device, onOpen }) {
  // A device is "online" if heartbeat is recent (< 10 min) AND active
  const hbAge = device.last_heartbeat_at
    ? (Date.now() - new Date(device.last_heartbeat_at).getTime()) / 1000
    : Infinity;
  const online = device.active !== false && hbAge < 600;
  const hb = device.heartbeat || {};
  const kiosk = hb.kiosk_alive;

  return (
    <div
      data-testid={`admin-mobile-door-${device.device_id}`}
      className={`rounded-2xl border-2 p-3 bg-white ${online ? 'border-emerald-300' : 'border-[#E2E2DF] opacity-80'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-[#1A3C34] truncate">{device.label || device.device_id}</h3>
            {online ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                <Wifi className="w-2.5 h-2.5" /> en ligne
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 px-1.5 py-0.5 rounded">
                <WifiOff className="w-2.5 h-2.5" /> hors ligne
              </span>
            )}
          </div>
          <p className="text-[10px] text-[#737373] font-mono truncate">
            {device.device_id}
            {hb.agent_version && <span className="ml-1">· v{hb.agent_version}</span>}
            {kiosk === true && <span className="ml-1 text-emerald-700">· kiosk OK</span>}
            {kiosk === false && <span className="ml-1 text-red-600">· kiosk HS</span>}
            {device.last_heartbeat_at && <span className="ml-1">· {fmtAgo(device.last_heartbeat_at)}</span>}
          </p>
        </div>
      </div>
      <button
        onClick={onOpen}
        disabled={!online}
        data-testid={`admin-mobile-open-${device.device_id}`}
        className={`w-full mt-3 h-14 rounded-xl font-medium text-base text-white flex items-center justify-center gap-2 active:scale-[0.98] transition ${
          online ? 'bg-[#1A3C34]' : 'bg-[#737373] cursor-not-allowed'
        }`}
      >
        <DoorOpen className="w-5 h-5" />
        Ouvrir ({online ? '5 s' : '—'})
      </button>
    </div>
  );
}

function isLive(b, hm) {
  if (!b.start_time || !b.end_time) return false;
  return b.start_time <= hm && hm < b.end_time;
}

function BookingRow({ b, live }) {
  return (
    <div
      data-testid={`admin-mobile-booking-${b.booking_id}`}
      className={`rounded-lg border p-2.5 bg-white flex items-start gap-2.5 ${
        live ? 'border-emerald-300 bg-emerald-50/40' : 'border-[#E2E2DF]'
      }`}
    >
      <div className="w-1 rounded-full bg-emerald-500 self-stretch" style={{ opacity: live ? 1 : 0 }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-[#1A3C34] truncate">{b.user_name || b.user_email || 'Anonyme'}</p>
          <span className="text-[10px] font-mono text-[#737373] flex items-center gap-1 flex-shrink-0">
            <Clock className="w-3 h-3" />
            {b.start_time} → {b.end_time}
          </span>
        </div>
        <p className="text-[11px] text-[#737373] truncate">
          {b.space_name}
          {live && <span className="ml-1.5 inline-flex items-center text-emerald-700 font-medium">● en cours</span>}
        </p>
      </div>
    </div>
  );
}
